import React from 'react';
import { MapPin, ArrowLeft, HousePlus, Calendar } from 'lucide-react';
import { Button } from '@rocco/components/button';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { useRootData } from '@modules/root';
import { formatBudgetLabel } from '@modules/build-hub/utils/format-budget';
import {
    formatScopeLabel,
    formatTypeLabel,
    formatProjectDateLabel,
} from '@modules/build-hub/utils/format-build-hub-attributes';
import { getGoogleStaticMapForBuildHub } from '@modules/build-hub/utils/get-google-static-map';
import { isStreetLevelAddress } from '@modules/build-hub/utils/is-street-level-address';
import { ReactComponent as MoneyIcon } from '@rocco/icons/svg/money.svg';
import { ReactComponent as HouseIcon } from '@rocco/icons/svg/house.svg';
import type { BuildHubTypeEnum } from 'generated/graphql';
import type { BuildHubFieldSelection } from '../../hooks/use-build-hub-field-selection';

interface EnquiryPreviewProps {
    buildHub: BuildHubOption;
    message: string;
    onBack: () => void;
    fieldSelection?: BuildHubFieldSelection;
}

export const EnquiryPreview: React.FC<EnquiryPreviewProps> = ({
    buildHub,
    message,
    onBack,
    fieldSelection,
}) => {
    const { config } = useRootData();

    const displayImage = buildHub.Thumbnail;

    // Generate map fallback if no image but location exists
    // Only show marker pin for street-level addresses
    const mapFallbackUrl =
        !displayImage && buildHub.Location
            ? getGoogleStaticMapForBuildHub(
                  buildHub.Location,
                  config.pixels.websiteGoogleMapKey,
                  {
                      showMarker: isStreetLevelAddress(buildHub.Location),
                      zoomLevel: 12,
                  },
              )
            : null;

    const budgetForFormatting = buildHub.Budget
        ? { min: buildHub.Budget.Min ?? 0, max: buildHub.Budget.Max ?? 0 }
        : null;

    // Get location display based on field selection
    const getLocationDisplay = (): string | null => {
        if (fieldSelection && !fieldSelection.includeLocation) {
            return null;
        }

        if (fieldSelection?.includeFullAddress) {
            // Show full address
            return (
                buildHub.LocationData?.fullAddress || buildHub.Location || null
            );
        } else {
            // Show region only (or full location if no LocationData)
            return buildHub.LocationData?.region || buildHub.Location || null;
        }
    };

    const locationDisplay = getLocationDisplay();

    // Build timeline display
    const timelineParts: string[] = [];
    if (
        (!fieldSelection || fieldSelection.includeStartDate) &&
        buildHub.StartDate
    ) {
        timelineParts.push(
            `Start: ${formatProjectDateLabel(buildHub.StartDate)}`,
        );
    }
    if (
        (!fieldSelection || fieldSelection.includeEndDate) &&
        buildHub.EndDate
    ) {
        timelineParts.push(`End: ${formatProjectDateLabel(buildHub.EndDate)}`);
    }
    const timelineDisplay =
        timelineParts.length > 0 ? timelineParts.join(' • ') : null;

    return (
        <div className="flex h-full flex-col">
            <div className="mb-6 flex items-center gap-2">
                <Button
                    color="transparent"
                    size="square40"
                    onClick={onBack}
                    className="-ml-2 shrink-0"
                >
                    <ArrowLeft className="size-5" />
                </Button>
                <h2 className="text-lg font-semibold">Message Preview</h2>
            </div>

            <div className="-mr-2 flex-1 overflow-y-auto pr-2">
                <div className="mb-8 flex flex-col gap-6 md:flex-row">
                    {/* Project Image */}
                    <div className="aspect-4/3 w-full shrink-0 overflow-hidden rounded-lg bg-gray-100 md:w-1/2">
                        {displayImage ? (
                            <img
                                src={displayImage}
                                alt={buildHub.Title}
                                className="h-full w-full object-cover"
                            />
                        ) : mapFallbackUrl ? (
                            <img
                                src={mapFallbackUrl}
                                alt={`Map of ${buildHub.Location}`}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400">
                                <div className="text-center text-gray-500">
                                    <HousePlus className="mx-auto mb-2 h-8 w-8" />
                                    <p className="text-sm">
                                        No image available
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Project Details */}
                    <div className="flex-1 space-y-6">
                        <h3 className="text-2xl font-bold">{buildHub.Title}</h3>

                        <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                    <HouseIcon className="size-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="mb-0.5 text-sm text-gray-500">
                                        Project type
                                    </p>
                                    <p className="font-medium">
                                        {formatTypeLabel(
                                            buildHub.ProjectType as BuildHubTypeEnum,
                                        ) ?? '-'}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                    <MapPin className="size-5 text-gray-600" />
                                </div>
                                <div>
                                    <p className="mb-0.5 text-sm text-gray-500">
                                        Project scope
                                    </p>
                                    <p className="font-medium">
                                        {formatScopeLabel(
                                            buildHub.ProjectScope || null,
                                        ) ?? '-'}
                                    </p>
                                </div>
                            </div>

                            {locationDisplay && (
                                <div className="flex items-start gap-3">
                                    <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                        <MapPin className="size-5 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="mb-0.5 text-sm text-gray-500">
                                            Location
                                        </p>
                                        <p className="font-medium">
                                            {locationDisplay}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {(!fieldSelection ||
                                fieldSelection.includeBudget) &&
                                budgetForFormatting && (
                                    <div className="flex items-start gap-3">
                                        <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                            <MoneyIcon className="size-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="mb-0.5 text-sm text-gray-500">
                                                Budget
                                            </p>
                                            <p className="font-medium">
                                                {formatBudgetLabel(
                                                    budgetForFormatting,
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}

                            {timelineDisplay && (
                                <div className="flex items-start gap-3">
                                    <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                        <Calendar className="size-5 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="mb-0.5 text-sm text-gray-500">
                                            Timeline
                                        </p>
                                        <p className="font-medium">
                                            {timelineDisplay}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {(!fieldSelection || fieldSelection.includeScope) &&
                                buildHub.ProjectScope && (
                                    <div className="flex items-start gap-3">
                                        <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                            <MapPin className="size-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="mb-0.5 text-sm text-gray-500">
                                                Scope
                                            </p>
                                            <p className="font-medium">
                                                {formatScopeLabel(
                                                    buildHub.ProjectScope,
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}

                            {(!fieldSelection || fieldSelection.includeStage) &&
                                buildHub.BuildStage && (
                                    <div className="flex items-start gap-3">
                                        <div className="shrink-0 rounded-full border border-gray-100 bg-white p-2 shadow-xs">
                                            <Calendar className="size-5 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="mb-0.5 text-sm text-gray-500">
                                                Stage
                                            </p>
                                            <p className="font-medium">
                                                {buildHub.BuildStage}
                                            </p>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>

                <div className="mb-2">
                    <p className="mb-2 text-xs font-medium tracking-wider text-gray-500 uppercase">
                        Message
                    </p>
                    <div className="min-h-[120px] rounded-lg border border-gray-200 bg-white p-4 whitespace-pre-wrap text-gray-700">
                        {message || 'No message entered.'}
                    </div>
                </div>
            </div>

            <div className="mt-6 border-t border-gray-100 pt-4">
                <Button color="secondary" onClick={onBack} className="gap-2">
                    <ArrowLeft className="size-4" />
                    Back
                </Button>
            </div>
        </div>
    );
};
